import React from 'react';
import { Pencil, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Resource } from '../../lib/resources';
import { getTwilioPhoneNumbers, TwilioPhoneNumbers } from '../../lib/resourcePhoneNumber';

// Add system_phone_number to the Resource type through augmentation
declare module '../../lib/resources' {
  interface Resource {
    system_phone_number?: string;
  }
}

interface BasicInformationProps {
  resource: Resource;
  editMode: string | null;
  formData: Partial<Resource>;
  setFormData: (data: Partial<Resource>) => void;
  onSave: (section: string) => void;
  onEdit: (section: string) => void;
  loading?: boolean;
}

interface Role {
  id: string;
  role: string;
}

export default function BasicInformation({
  resource,
  editMode,
  formData,
  setFormData,
  onSave,
  onEdit,
  loading
}: BasicInformationProps) {
  const [roles, setRoles] = React.useState<Role[]>([]);
  const [rolesLoading, setRolesLoading] = React.useState(false);
  const [rolesError, setRolesError] = React.useState<string | null>(null);
  const [twilioPhoneNumbers, setTwilioPhoneNumbers] = React.useState<TwilioPhoneNumbers | null>(null);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = React.useState(false);

  const loadRoles = async () => {
    if (!resource.business_id || !supabase) return;
    
    try {
      setRolesLoading(true);
      setRolesError(null);
      
      const { data, error } = await supabase
        .from('resource_permissionsv2')
        .select('id, role')
        .eq('business_id', resource.business_id);

      if (error) throw error;
      setRoles(data || []);
    } catch (err) {
      console.error('Error loading roles:', err);
      setRolesError(err instanceof Error ? err.message : 'Failed to load roles');
    } finally {
      setRolesLoading(false);
    }
  };

  const loadTwilioPhoneNumbers = async () => {
    if (!resource.business_id) return;
    
    try {
      setPhoneNumbersLoading(true);
      const numbers = await getTwilioPhoneNumbers(resource.business_id);
      setTwilioPhoneNumbers(numbers);
      
      // Set initial selected phone number if resource has one
      if (resource.system_phone_number) {
        setFormData({ ...formData, system_phone_number: resource.system_phone_number });
      }
    } catch (err) {
      console.error('Error loading Twilio phone numbers:', err);
    } finally {
      setPhoneNumbersLoading(false);
    }
  };

  React.useEffect(() => {
    if (editMode === 'basic') {
      loadRoles();
      loadTwilioPhoneNumbers();
    }
  }, [editMode, resource.business_id]);

  // Update this to actually update the formData
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedNumber = e.target.value;
    // Update the formData so it gets saved when the user clicks "Save"
    setFormData({ ...formData, system_phone_number: selectedNumber });
    console.log('Phone number selected and added to form data:', selectedNumber);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-900">Basic Information</h3>
        {editMode === 'basic' ? (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEdit('')}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave('basic')}
              disabled={loading}
              className="text-xs text-blue-600 hover:text-blue-700"
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <button
            onClick={() => onEdit('basic')}
            className="text-gray-400 hover:text-gray-600"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="p-4">
        <div className="grid grid-cols-2 gap-4">
          {editMode === 'basic' ? (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.first_name || ''}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.last_name || ''}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                />
              </div>
              {/* Role field hidden for now
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Role
                </label>
                {rolesLoading ? (
                  <div className="animate-pulse bg-gray-100 h-9 rounded-md"></div>
                ) : rolesError ? (
                  <div className="flex items-center space-x-2 text-red-600 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    <span>Error loading roles</span>
                  </div>
                ) : roles.length > 0 ? (
                  <select
                    value={formData.role || ''}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  >
                    <option value="">Select a role</option>
                    {roles.map(role => (
                      <option key={role.id} value={role.role}>
                        {role.role}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-sm text-gray-500">
                    No roles available
                  </div>
                )}
              </div>
              */}
              {/* System Phone Number field hidden for now
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  System Phone Number
                </label>
                {phoneNumbersLoading ? (
                  <div className="animate-pulse bg-gray-100 h-9 rounded-md"></div>
                ) : (
                  <select
                    value={formData.system_phone_number || ''}
                    onChange={handlePhoneNumberChange}
                    className="w-full h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                  >
                    <option value="">Select a phone number</option>
                    {twilioPhoneNumbers?.mainNumber && (
                      <option value={twilioPhoneNumbers.mainNumber}>
                        Main System Phone Number: {twilioPhoneNumbers.mainNumber}
                      </option>
                    )}
                    {twilioPhoneNumbers?.additionalNumbers && twilioPhoneNumbers.additionalNumbers.length > 0 && (
                      <optgroup label="Additional Numbers">
                        {twilioPhoneNumbers.additionalNumbers.map((number, index) => (
                          <option key={index} value={number}>
                            {number}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                )}
              </div>
              */}
            </>
          ) : (
            <>
              <div>
                <div className="text-xs text-gray-500">First Name</div>
                <div className="text-sm text-gray-900 mt-1">{resource.first_name}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Last Name</div>
                <div className="text-sm text-gray-900 mt-1">{resource.last_name}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Email</div>
                <div className="text-sm text-gray-900 mt-1">{resource.email}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Phone</div>
                <div className="text-sm text-gray-900 mt-1">{resource.phone}</div>
              </div>
              {/* Role field hidden for now
              <div>
                <div className="text-xs text-gray-500">Role</div>
                <div className="text-sm text-gray-900 mt-1">
                  {resource.role || 'No role assigned'}
                </div>
              </div>
              */}
              {/* System Phone Number field hidden for now
              <div>
                <div className="text-xs text-gray-500">System Phone Number</div>
                <div className="text-sm text-gray-900 mt-1">
                  {resource.system_phone_number || 'None assigned'}
                </div>
              </div>
              */}
            </>
          )}
        </div>
      </div>
    </div>
  );
}